<div class="blast">
  <div class="blast-head">
    <span class="blast-title">Blast radius</span>
    @if (rootDegree() !== null) {
      <span class="blast-degree">{{ rootDegree() | number }} relationships</span>
    }
  </div>

  @if (loading()) {
    <div class="blast-state">
      <span class="spinner-border spinner-border-sm text-secondary"></span>
      <span>Matching assets to inventory…</span>
    </div>
  } @else if (!hasAnything()) {
    <!--
      Why there is nothing, not just that there is nothing. An alert about an IP
      pair naming no asset is the shape of the alert; a case that should name one
      and does not is a data-quality gap. Different findings, different wording.
    -->
    <div class="blast-empty">
      <i class="bi bi-diagram-3"></i>
      <div>
        <div class="blast-empty-title">{{ emptyTitle() }}</div>
        <div class="blast-empty-body">{{ emptyBody() }}</div>
      </div>
    </div>
  } @else {
    <!--
      Radial, and every connector is a plain line — no arrowheads, no ordering.
      This is what the asset touches, which has no chronology; anything
      directional would read as a sequence of events it does not describe.
    -->
    <div
      class="blast-canvas"
      [style.height.px]="canvasHeight()"
    >
      <svg
        [attr.viewBox]="viewBox()"
        [style.height.px]="canvasHeight()"
        role="img"
        [attr.aria-label]="'Blast radius of ' + (root() ? displayName(root()!) : '')"
      >
        @for (m of marks(); track m.kind) {
          <line
            class="blast-edge"
            x1="0"
            [attr.y1]="rootNode()?.y ?? 0"
            [attr.x2]="m.x"
            [attr.y2]="m.y"
          />
        }
      </svg>

      @if (rootNode(); as r) {
        <button
          type="button"
          class="blast-node is-root"
          [class.is-unresolved]="!isResolved(r.resolved)"
          [style.top]="r.top"
          [style.--node-color]="r.color"
          [title]="r.kindLabel + ' · ' + r.resolved.origin.sourceField + ' · rooted from the case'"
          (click)="onExplore(r.resolved, r.intents[0])"
        >
          <span class="blast-node-dot"></span>
          <span class="blast-node-label">{{ displayName(r.resolved) }}</span>
          <span class="blast-node-kind">{{ r.kindLabel }}</span>
        </button>
      }

      <!--
        One mark per relation kind, carrying its true count. These are edges out
        of the graph, so they are the only marks a connector reaches.
      -->
      @for (m of marks(); track m.kind) {
        <div
          class="blast-sat"
          [style.left]="m.left"
          [style.top]="m.top"
        >
          <button
            type="button"
            class="blast-node is-relation"
            [style.--node-color]="m.color"
            [title]="m.intent.description"
            (click)="onExploreMark(m)"
          >
            <span class="blast-node-dot"></span>
            <span class="blast-node-count">{{ m.count | number }}</span>
            <span class="blast-node-kind">{{ m.label }}</span>
          </button>
        </div>
      }
    </div>

    @if (rootNode(); as r) {
      @if (isResolved(r.resolved)) {
        @if (!marks().length && !reachLoading()) {
          <!--
            Resolved, but touching nothing we can see. Stated rather than left as
            an empty circle: "no relationships" and "no source feeding this
            entity's relationships" look identical on a blank canvas and mean
            opposite things.
          -->
          <div class="blast-none-note">
            <i class="bi bi-info-circle"></i>
            <span>
              No relationships are recorded for
              <strong>{{ displayName(r.resolved) }}</strong>
              yet.
            </span>
          </div>
        }

        <!--
          Intents, listed rather than hidden behind the graph. The point of a
          preset is that it is chosen fast; making the user hover a small circle
          to discover it would put back the configuration it exists to remove.
        -->
        <div class="blast-intents">
          @for (intent of r.intents; track intent.key) {
            <button
              type="button"
              class="blast-intent"
              [title]="intent.description"
              (click)="onExplore(r.resolved, intent)"
            >
              {{ intent.label }}
              <i class="bi bi-arrow-right-short"></i>
            </button>
          }
        </div>
      } @else {
        <!--
          Named but not tracked. Said plainly: a case about an asset that is not
          in inventory is a coverage gap, not a rendering failure.
        -->
        <div class="blast-unresolved-note">
          <i class="bi bi-exclamation-triangle"></i>
          <span>
            <strong>{{ displayName(r.resolved) }}</strong>
            is not in your inventory, so its relationships cannot be explored.
          </span>
        </div>
      }
    }

    <!--
      Other assets the same ticket names. A LIST, not marks on the graph: the
      ticket mentioning two things is not a claim that the graph connects them,
      and a connector would have said that it did.
    -->
    @if (alsoNamed().length) {
      <div class="blast-also">
        <span class="blast-also-label">Also named on this case</span>
        <div class="blast-also-items">
          @for (o of alsoNamed(); track o.origin.name) {
            <button
              type="button"
              class="blast-also-item"
              [class.is-unresolved]="!isResolved(o)"
              [disabled]="!isResolved(o)"
              [style.--node-color]="colorFor(o)"
              [title]="
                isResolved(o)
                  ? 'Open the blast radius of this ' + kindLabels[o.origin.kind].toLowerCase()
                  : kindLabels[o.origin.kind] + ' · not found in inventory'
              "
              (click)="onExploreOther(o)"
            >
              <span class="blast-also-dot"></span>
              <span class="blast-also-name">{{ displayName(o) }}</span>
              @if (o.degree !== null) {
                <span class="blast-also-degree">{{ o.degree | number }}</span>
              }
            </button>
          }
        </div>
      </div>
    }
  }
</div>
